import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Clock, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STEP_LABELS = {
  atualizacao_cadastral: 'Atualização Cadastral',
  atualizacao_endereco: 'Atualização de Endereço',
  atualizacao_dados: 'Atualização dos Dados',
  vida_financeira: 'Vida Financeira',
  verificar_email: 'Verificar E-mail',
  antes: 'ANTES (Rating Inicial)',
  cadastro: 'CADASTRO (Estruturação)',
  depois: 'DEPOIS (Rating Final)',
  servico_entregue: 'Serviço Entregue'
};

const ServiceSteps = ({ leadId, leadName }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    const fetchSteps = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/service-steps/lead/${leadId}`);
        const data = await response.json();
        
        if (data.success) {
          setSteps(data.steps);
          // Inicializar notas
          const initialNotes = {};
          data.steps.forEach(step => {
            if (step.notes) {
              initialNotes[step.step_name] = step.notes;
            }
          });
          setNotes(initialNotes);
        } else {
          setError(data.error || 'Erro ao buscar etapas');
        }
      } catch (error) {
        setError('Erro ao conectar com o servidor');
        console.error('Erro ao buscar etapas:', error);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchSteps();
    }
  }, [leadId]);

  const toggleStep = async (stepName, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/service-steps/lead/${leadId}/step/${stepName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          completed: !currentStatus,
          notes: notes[stepName] || ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Atualizar estado local
        setSteps(steps.map(step => 
          step.step_name === stepName 
            ? { ...step, completed: !currentStatus, completed_at: !currentStatus ? new Date().toISOString() : null }
            : step
        ));
      }
    } catch (err) {
      setError('Erro ao atualizar etapa');
    }
  };

  const updateNote = (stepName, value) => {
    setNotes(prev => ({
      ...prev,
      [stepName]: value
    }));
  };

  const saveNote = async (stepName) => {
    try {
      const step = steps.find(s => s.step_name === stepName);
      const response = await fetch(`${API_URL}/service-steps/lead/${leadId}/step/${stepName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          completed: step.completed,
          notes: notes[stepName] || ''
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Atualizar estado local
        setSteps(steps.map(s => 
          s.step_name === stepName 
            ? { ...s, notes: notes[stepName] }
            : s
        ));
      }
    } catch (err) {
      setError('Erro ao salvar nota');
    }
  };

  const getProgress = () => {
    if (steps.length === 0) return 0;
    const completed = steps.filter(step => step.completed).length;
    return Math.round((completed / steps.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Clock className="animate-spin" size={24} />
        <span className="ml-2">Carregando etapas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-red-600">
        <AlertCircle size={24} />
        <span className="ml-2">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Etapas do Serviço</h2>
          <p className="text-gray-600">{leadName}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">{getProgress()}%</div>
          <div className="text-sm text-gray-500">Concluído</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-4">
        <div 
          className="bg-blue-600 h-4 rounded-full transition-all duration-300"
          style={{ width: `${getProgress()}%` }}
        />
      </div>

      {/* Steps List */}
      <div className="space-y-3">
        {steps.map((step, index) => (
          <div 
            key={step.step_name}
            className={`border rounded-lg p-4 transition-all ${
              step.completed 
                ? 'bg-green-50 border-green-200' 
                : 'bg-white border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-start gap-4">
              {/* Checkbox */}
              <button
                onClick={() => toggleStep(step.step_name, step.completed)}
                className={`mt-1 ${
                  step.completed ? 'text-green-600' : 'text-gray-400 hover:text-blue-600'
                }`}
              >
                {step.completed ? (
                  <CheckCircle size={24} />
                ) : (
                  <Circle size={24} />
                )}
              </button>

              {/* Step Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">
                    {index + 1}. {STEP_LABELS[step.step_name] || step.step_name}
                  </span>
                  {step.completed && (
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                      Concluído
                    </span>
                  )}
                </div>

                {/* Notes Input */}
                <div className="mt-2">
                  <textarea
                    value={notes[step.step_name] || step.notes || ''}
                    onChange={(e) => updateNote(step.step_name, e.target.value)}
                    onBlur={() => saveNote(step.step_name)}
                    placeholder="Adicionar notas..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={2}
                  />
                </div>

                {/* Timestamp */}
                {step.completed_at && (
                  <div className="mt-2 text-xs text-gray-500">
                    Concluído em: {new Date(step.completed_at).toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceSteps;
